import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { User, StreamState, SeekOption } from '../types';
import { Header } from './Header';
import { VideoPlayer } from './VideoPlayer';
import { TimeTravel } from './TimeTravel';
import { getKvsStreamingUrl } from '../services/kvs';

const RABBIT_FACTOIDS = [
  'Rabbits express joy by doing a "binky," which is a jump and twist in mid-air. Keep an eye out for one!',
  'A rabbit\'s teeth never stop growing. They need to chew on hay and grass to keep them worn down.',
  'Rabbits can see nearly 360 degrees around them, but they have a small blind spot right in front of their nose.',
  'A group of rabbits is called a "fluffle." How adorable is that?',
  'Rabbits purr when they\'re happy, similar to cats! They do it by gently grinding their teeth together.',
  'Baby rabbits are called "kittens" or "kits," not bunnies!',
  'Rabbits are crepuscular, meaning they\'re most active at dawn and dusk.',
  'A rabbit\'s ears can rotate 180 degrees independently to pinpoint sounds from any direction.',
  'Rabbits can jump up to 3 feet high and 9 feet long in a single bound!',
  'Rabbits have over 17,000 taste buds, compared to humans who have about 10,000.',
];

/** Get the current access token from Cognito */
async function getAccessToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString();
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return undefined;
  }
}

const SKIP_SECONDS = 15;
const PLAYBACK_WINDOW_MS = 60 * 60 * 1000; // 1 hour window

/** Convert SeekOption to hours offset */
function seekOptionToHours(option: SeekOption): number {
  const match = option.match(/^-(\d+)h$/);
  return match ? parseInt(match[1], 10) : 0;
}

/** Calculate start/end timestamps for historical playback with optional offset */
function getHistoricalTimestamps(option: SeekOption, offsetMs: number = 0): { startTimestamp: number; endTimestamp: number } {
  const hoursAgo = seekOptionToHours(option);
  const now = Date.now();
  // Base start time from the seek option
  const baseStartTimestamp = now - (hoursAgo * 60 * 60 * 1000);
  // Apply offset (positive = forward in time, negative = backward)
  const startTimestamp = baseStartTimestamp + offsetMs;
  // End at 1 hour after start, or now if that would be in the future
  const endTimestamp = Math.min(startTimestamp + PLAYBACK_WINDOW_MS, now);
  return { startTimestamp, endTimestamp };
}

interface DashboardProps {
  user: User;
  onSignOut: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onSignOut }) => {
  const [streamState, setStreamState] = useState<StreamState>({
    isPlaying: true,
    isMuted: true,
    isLive: true,
    currentSeek: null,
    volume: 1,
  });

  const [streamingUrl, setStreamingUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track offset from the base seek position (in milliseconds)
  const [playbackOffset, setPlaybackOffset] = useState(0);

  // Select a random factoid once on mount
  const randomFactoid = useMemo(
    () => RABBIT_FACTOIDS[Math.floor(Math.random() * RABBIT_FACTOIDS.length)],
    []
  );

  const fetchStreamingUrl = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const authToken = await getAccessToken();
      const response = await getKvsStreamingUrl({ authToken });
      setStreamingUrl(response.streamingUrl);
      setStreamState(prev => ({ ...prev, isLive: true, isPlaying: true }));
    } catch (err) {
      console.error('Error fetching streaming URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stream');
      setStreamingUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreamingUrl();
  }, [fetchStreamingUrl]);

  const togglePlay = () => {
    setStreamState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
  };

  const handleSeek = useCallback(async (option: SeekOption) => {
    setIsLoading(true);
    setError(null);
    setPlaybackOffset(0); // Reset offset when changing time periods
    setStreamState(prev => ({
      ...prev,
      isLive: false,
      currentSeek: option,
      isPlaying: true
    }));

    try {
      const { startTimestamp, endTimestamp } = getHistoricalTimestamps(option, 0);
      console.log(`Time Travel: ${option} (${new Date(startTimestamp).toLocaleString()} - ${new Date(endTimestamp).toLocaleString()})`);

      const authToken = await getAccessToken();
      const response = await getKvsStreamingUrl({ authToken, startTimestamp, endTimestamp });
      setStreamingUrl(response.streamingUrl);
    } catch (err) {
      console.error('Error fetching historical stream:', err);
      setError(err instanceof Error ? err.message : 'Failed to load historical stream');
      setStreamingUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const goLive = () => {
    // Refresh the streaming URL to get back to live
    setPlaybackOffset(0);
    fetchStreamingUrl();
    setStreamState(prev => ({
      ...prev,
      isLive: true,
      currentSeek: null,
      isPlaying: true
    }));
  };

  // Skip back 15 seconds
  const handleSkipBack = useCallback(async () => {
    const newOffset = playbackOffset - (SKIP_SECONDS * 1000);
    setPlaybackOffset(newOffset);
    setIsLoading(true);

    try {
      const authToken = await getAccessToken();
      if (streamState.isLive) {
        // From live, go to 15 seconds ago
        const now = Date.now();
        const startTimestamp = now - (SKIP_SECONDS * 1000);
        const endTimestamp = now;
        console.log(`Skip back from LIVE: ${new Date(startTimestamp).toLocaleString()}`);

        const response = await getKvsStreamingUrl({ authToken, startTimestamp, endTimestamp });
        setStreamingUrl(response.streamingUrl);
        setStreamState(prev => ({
          ...prev,
          isLive: false,
          currentSeek: null,
          isPlaying: true
        }));
      } else if (streamState.currentSeek) {
        // In historical mode, adjust the window
        const { startTimestamp, endTimestamp } = getHistoricalTimestamps(streamState.currentSeek, newOffset);
        console.log(`Skip back in ${streamState.currentSeek}: ${new Date(startTimestamp).toLocaleString()}`);

        const response = await getKvsStreamingUrl({ authToken, startTimestamp, endTimestamp });
        setStreamingUrl(response.streamingUrl);
      }
    } catch (err) {
      console.error('Error skipping back:', err);
      setError(err instanceof Error ? err.message : 'Failed to skip');
    } finally {
      setIsLoading(false);
    }
  }, [playbackOffset, streamState.isLive, streamState.currentSeek]);

  // Skip forward 15 seconds (only in historical mode)
  const handleSkipForward = useCallback(async () => {
    if (streamState.isLive) return; // Can't skip forward from live

    const newOffset = playbackOffset + (SKIP_SECONDS * 1000);
    setPlaybackOffset(newOffset);
    setIsLoading(true);

    try {
      if (streamState.currentSeek) {
        const { startTimestamp, endTimestamp } = getHistoricalTimestamps(streamState.currentSeek, newOffset);
        const now = Date.now();

        // Check if we've caught up to live
        if (startTimestamp >= now - 5000) {
          console.log('Caught up to live');
          goLive();
          return;
        }

        console.log(`Skip forward in ${streamState.currentSeek}: ${new Date(startTimestamp).toLocaleString()}`);

        const authToken = await getAccessToken();
        const response = await getKvsStreamingUrl({ authToken, startTimestamp, endTimestamp });
        setStreamingUrl(response.streamingUrl);
      }
    } catch (err) {
      console.error('Error skipping forward:', err);
      setError(err instanceof Error ? err.message : 'Failed to skip');
    } finally {
      setIsLoading(false);
    }
  }, [playbackOffset, streamState.isLive, streamState.currentSeek, goLive]);

  const connectionStatus = isLoading ? 'connecting' : (streamingUrl && !error) ? 'online' : 'offline';

  return (
    <div className="min-h-screen bg-rutabaga-bg">
      <Header user={user} onSignOut={onSignOut} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-6">
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between mb-4 px-1">
                <h2 className="text-2xl font-heading font-bold text-rutabaga-text">
                  Hutch Cam <span className="text-gray-400 font-normal text-lg ml-2">Main View</span>
                </h2>
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      connectionStatus === 'online' ? 'bg-green-500' :
                      connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                      'bg-red-500'
                    }`}></span>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      {connectionStatus === 'online' ? 'Online' :
                       connectionStatus === 'connecting' ? 'Connecting' :
                       'Offline'}
                    </span>
                </div>
              </div>

              <VideoPlayer
                streamState={streamState}
                streamingUrl={streamingUrl}
                isLoading={isLoading}
                error={error}
                togglePlay={togglePlay}
                goLive={goLive}
                onRetry={fetchStreamingUrl}
                onSkipBack={handleSkipBack}
                onSkipForward={handleSkipForward}
              />
            </div>

            <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
               <TimeTravel currentSeek={streamState.currentSeek} onSeek={handleSeek} />
            </div>
          </div>

          {/* Sidebar / Info Column */}
          <div className="lg:col-span-4 space-y-6 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

            {/* Did you know? */}
             <div className="bg-rutabaga-wood/10 rounded-2xl p-6 border border-rutabaga-wood/20">
              <h3 className="font-heading font-bold text-lg mb-2 text-rutabaga-woodDark">Did you know?</h3>
              <p className="text-sm text-rutabaga-woodDark/80 leading-relaxed">
                {randomFactoid}
              </p>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};